import type { LegEditLocks } from "@/lib/leg-edit-rules";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TimeInput } from "@/components/ui/time-input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Package, ArrowRight, Plus, Trash2, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { LocationPicker } from "@/components/forms/LocationPicker";
import { OrderFormData, LegData, addMinutesLocal, DEFAULT_WINDOW_MINUTES } from "@/components/orders/orderFormTypes";
import { Location, Tractor, Driver } from "@/types/tms";
import { formLocalToUtc, planningTzAbbr } from "@/lib/timezone";
import {
  stopDurationHours,
  formatDurationWarning,
  DURATION_WARNING_THRESHOLD_HOURS,
} from "@/lib/order-duration-warning";
import { TractorCombobox } from "@/components/orders/TractorCombobox";
import { DriverCombobox } from "@/components/orders/DriverCombobox";
import { computeOosBlockedIdsForWindow } from "@/hooks/useTractorOosStatusMap";
import {
  computeEligibilityReasons,
  type EligibilityGateState,
  type TruckEligibilityRow,
} from "@/lib/truck-eligibility";
import { TrailerIdField, type TrailerNeedsServiceBlock } from "@/components/orders/TrailerIdField";
import { TrailerTypeField } from "@/components/orders/TrailerTypeField";
import { ShuttleCarrierFields } from "@/components/orders/form/ShuttleCarrierFields";
import { EquipmentAdvancedFields } from "@/components/orders/form/EquipmentAdvancedFields";
import type { LoadStatusValue } from "@/components/orders/orderFormTypes";
import { tripTypeOptionsForTenant } from "@/lib/feature-flags";
import {
  handleShipFromLocationChange,
  handleShipToLocationChange,
  handleLegLocationChange,
  updateLeg,
  addLeg,
  removeLeg,
  cascadeDeliveryToNextReady,
  MIN_LEG_GAP_MINUTES,
} from "@/hooks/useOrderFormLegLogic";

/** CST/CDT label for a time field. Order times are always entered in Central,
 *  so the label depends only on the *date being entered* (a December date
 *  reads CST even when today is CDT), never on the stop. */
function TzChip({ value }: { value: string | undefined }) {
  const abbr = planningTzAbbr(value);
  return (
    <span className="ml-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      ({abbr})
    </span>
  );
}

/** Lexicographic max ignoring undefined. Works for `yyyy-MM-dd` and `HH:mm`
 *  strings since both formats sort correctly as strings. */
function maxStr(a?: string, b?: string): string | undefined {
  if (a && b) return a > b ? a : b;
  return a ?? b;
}

/** Generic Start + End date-time row used by both single- and multi-leg stops.
 *  Renders the four inputs (start date, start time, end date, end time) in a
 *  single horizontal flex row that wraps on narrow viewports. */
function WindowRow({
  start,
  end,
  onStartChange,
  onEndChange,
  required,
  minStartTime,
  minStartDate,
  disabled,
  nowFloor,
}: {
  start: string;
  end: string;
  onStartChange: (next: string) => void;
  onEndChange: (next: string) => void;
  required?: boolean;
  minStartTime?: string;
  minStartDate?: string;
  disabled?: boolean;
  nowFloor?: { date: string; time: string };
}) {
  const startDate = start ? start.substring(0, 10) : "";
  const startTime = start ? start.substring(11, 16) : "";
  const endDate = end ? end.substring(0, 10) : "";
  const endTime = end ? end.substring(11, 16) : "";
  // Native `min` on <input type="date"> triggers a browser validation bubble on
  // submit. Never apply the "now" floor to a value that is *already* in the past
  // (pre-existing order data) — those are grandfathered and handled by
  // form-level validation, which skips unchanged past values.
  const startNowFloorDate =
    nowFloor && (!startDate || startDate >= nowFloor.date) ? nowFloor.date : undefined;
  const endNowFloorDate =
    nowFloor && (!endDate || endDate >= nowFloor.date) ? nowFloor.date : undefined;
  const effectiveMinDate = maxStr(
    minStartDate && startDate && startDate < minStartDate ? undefined : minStartDate,
    startNowFloorDate,
  );
  // Only apply the minStartTime floor when the start date matches the date
  // that floor was derived from (e.g. previous leg's delivery date, or the
  // sibling ready_time date). On any later calendar day, any time is valid.
  const minStartTimeForDate =
    minStartTime && minStartDate && startDate && startDate === minStartDate
      ? minStartTime
      : undefined;
  const startTimeFloor =
    startNowFloorDate && startDate && startDate === nowFloor!.date
      ? maxStr(minStartTimeForDate, nowFloor!.time)
      : minStartTimeForDate;
  const endNowFloorTime =
    endNowFloorDate && endDate && endDate === nowFloor!.date ? nowFloor!.time : undefined;
  const endTimeFloor =
    endDate && startDate && endDate === startDate
      ? maxStr(startTime, endNowFloorTime)
      : endNowFloorTime;
  const endMinDate = maxStr(
    endDate && startDate && endDate < startDate ? undefined : startDate || undefined,
    endNowFloorDate,
  );

  return (
    <div className="flex flex-wrap gap-2 items-end">
      <div className="flex flex-col gap-1 min-w-0">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Start
        </span>
        <div className="flex gap-2 items-center">
          <Input
            type="date"
            value={startDate}
            min={effectiveMinDate}
            onChange={(e) => {
              const time = startTime || "08:00";
              onStartChange(e.target.value ? `${e.target.value}T${time}` : "");
            }}
            required={required}
            disabled={disabled}
            className="w-[150px]"
          />
          <TimeInput
            value={startTime}
            onChange={(time) => {
              const date = startDate || format(new Date(), "yyyy-MM-dd");
              onStartChange(`${date}T${time}`);
            }}
            required={required}
            disabled={disabled}
            minTime={startTimeFloor}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1 min-w-0">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          End
        </span>
        <div className="flex gap-2 items-center">
          <Input
            type="date"
            value={endDate}
            min={endMinDate}
            onChange={(e) => {
              const time = endTime || startTime || "09:00";
              onEndChange(e.target.value ? `${e.target.value}T${time}` : "");
            }}
            required={required}
            disabled={disabled}
            className="w-[150px]"
          />
          <TimeInput
            value={endTime}
            onChange={(time) => {
              const date = endDate || startDate || format(new Date(), "yyyy-MM-dd");
              onEndChange(`${date}T${time}`);
            }}
            required={required}
            disabled={disabled}
            minTime={endTimeFloor}
          />
        </div>
      </div>
    </div>
  );
}

/** Apply a new start to a window, shifting end forward to maintain at least the
 *  current window length (or the default 1h if end is unset / behind start). */
function shiftWindowStart(prevStart: string, prevEnd: string, newStart: string): { start: string; end: string } {
  if (!newStart) return { start: "", end: prevEnd };
  let span = DEFAULT_WINDOW_MINUTES;
  if (prevStart && prevEnd && prevStart.includes("T") && prevEnd.includes("T")) {
    const a = Date.parse(prevStart + ":00Z");
    const b = Date.parse(prevEnd + ":00Z");
    if (Number.isFinite(a) && Number.isFinite(b) && b >= a) {
      span = Math.max(0, Math.round((b - a) / 60_000));
    }
  }
  return { start: newStart, end: addMinutesLocal(newStart, span) };
}

/** Clamp end so it's never before start. */
function clampEnd(start: string, end: string): string {
  if (!start || !end) return end;
  const a = Date.parse(start + ":00Z");
  const b = Date.parse(end + ":00Z");
  if (Number.isFinite(a) && Number.isFinite(b) && b < a) return start;
  return end;
}

interface OrderStopsSectionProps {
  formData: OrderFormData;
  setFormData: React.Dispatch<React.SetStateAction<OrderFormData>>;
  locations: Location[] | undefined;
  /** Whether to allow adding new stops (false for edit dialog) */
  allowAddStops?: boolean;
  /** Whether origin/destination location pickers should be disabled */
  locationsDisabled?: boolean;
  /** Whether time windows + per-leg dispatch fields (trip type, tractor,
   *  operators) should be disabled. Split from `locationsDisabled` so API-
   *  created orders can keep origin/destination locked but still allow
   *  editing ready/delivery windows and dispatch assignments. Defaults to
   *  `locationsDisabled` for backwards compatibility. */
  timesDisabled?: boolean;
  /** Tractors for per-leg asset pickers (multi-leg only). */
  tractors?: Tractor[];
  /** Drivers for per-leg asset pickers (multi-leg only). */
  drivers?: Driver[];
  /** Active tenant slug — used to gate the "CI" trip type to pre-prod. */
  activeTenant?: string;
  /** Tractor ids that should render disabled as booked in asset pickers. */
  disabledTractorIds?: Set<string>;
  /** Per-leg disabled tractor ids (index-aligned with `formData.legs`).
   *  Takes precedence over `disabledTractorIds` for per-leg pickers so a
   *  conflict on one leg never blocks a non-overlapping sibling leg. */
  disabledTractorIdsByLeg?: Set<string>[];
  /** Per-tractor OOS status for per-leg asset pickers. See TractorCombobox. */
  tractorOosStatusById?: import("@/lib/tractor-service-status").TractorServiceStatus extends never
    ? never
    : Map<string, import("@/lib/tractor-service-status").TractorServiceStatus>;
  /** Per-tractor active service records, keyed by tractor id. Used to compute
   *  per-leg window-aware OOS blocking. */
  tractorOosRecordsById?: Map<
    string,
    import("@/lib/tractor-service-status").ServiceRecordLike[]
  >;
  /** Latest AAM eligibility event per tractor. Used to block tractors whose
   *  eligible mission modes don't include the leg's effective trip type. */
  tractorEligibilityByTractor?: Map<string, TruckEligibilityRow>;
  /** Eligibility feature state; blocks selection while loading/erroring. */
  tractorEligibilityGateState?: EligibilityGateState;
  /** When set, date/time pickers floor to this LA-local "now" so users can't
   *  pick past slots. Pass `undefined` for edit/recurring flows. */
  nowFloor?: { date: string; time: string };
  /** Required for the per-leg Trailer ID picker to surface a shared
   *  Needs-Service block dialog in the parent form. */
  onTrailerNeedsServiceBlock?: (info: TrailerNeedsServiceBlock) => void;
  /** Renders the "advanced" (Order API parity) per-stop inputs: shuttle
   *  carrier on each stop and, for multi-leg, per-leg equipment state.
   *  Gated upstream by the form's "Advanced fields" toggle. */
  showAdvancedFields?: boolean;
  /** Per-leg edit locks (index-aligned with `formData.legs`), from
   *  `src/lib/leg-edit-rules.ts`. When supplied they replace the form-wide
   *  `locationsDisabled` / `timesDisabled` flags for that stop, so a stop
   *  that is already delivered or in transit is frozen while its open
   *  siblings stay editable. Form-wide flags still apply on top (OR). */
  legLocksByIndex?: LegEditLocks[];
  /** Saved trailer ID per leg id (edit flow), so unchanged trailers are never re-defaulted. */
  legTrailerBaselineById?: Record<string, string>;
}

export function OrderStopsSection({
  formData,
  setFormData,
  locations,
  allowAddStops = true,
  locationsDisabled = false,
  timesDisabled,
  tractors,
  drivers,
  activeTenant,
  disabledTractorIds,
  disabledTractorIdsByLeg,
  tractorOosStatusById,
  tractorOosRecordsById,
  tractorEligibilityByTractor,
  tractorEligibilityGateState = "off",
  nowFloor,
  onTrailerNeedsServiceBlock,
  showAdvancedFields = false,
  legLocksByIndex,
  legTrailerBaselineById,
}: OrderStopsSectionProps) {
  const resolvedTimesDisabled = timesDisabled ?? locationsDisabled;
  if (formData.order_type === "single-leg") {
    return (
      <>
      <PlanningTzNote />
      <SingleLegStops
        formData={formData}
        setFormData={setFormData}
        locations={locations}
        locationsDisabled={locationsDisabled}
        timesDisabled={resolvedTimesDisabled}
        nowFloor={nowFloor}
        showAdvancedFields={showAdvancedFields}
      />
      </>
    );
  }
  return (
    <>
    <PlanningTzNote />
    <MultiLegStops
      formData={formData}
      setFormData={setFormData}
      locations={locations}
      allowAddStops={allowAddStops}
      locationsDisabled={locationsDisabled}
      timesDisabled={resolvedTimesDisabled}
      tractors={tractors}
      drivers={drivers}
      activeTenant={activeTenant}
      disabledTractorIds={disabledTractorIds}
      disabledTractorIdsByLeg={disabledTractorIdsByLeg}
      tractorOosStatusById={tractorOosStatusById}
      tractorOosRecordsById={tractorOosRecordsById}
      tractorEligibilityByTractor={tractorEligibilityByTractor}
      tractorEligibilityGateState={tractorEligibilityGateState}
      nowFloor={nowFloor}
      onTrailerNeedsServiceBlock={onTrailerNeedsServiceBlock}
      showAdvancedFields={showAdvancedFields}
      legLocksByIndex={legLocksByIndex}
      legTrailerBaselineById={legTrailerBaselineById}
    />
    </>
  );
}

/** One-line reminder that order times are typed in Central, whoever and
 *  wherever the user is. Saved times are shown elsewhere in the viewer's own
 *  timezone, so without this the same order can look like it has two times. */
export function PlanningTzNote() {
  return (
    <p className="mb-3 text-xs text-muted-foreground" data-testid="planning-tz-note">
      All times are entered in Central time ({planningTzAbbr(null)}).
    </p>
  );
}

function SingleLegStops({
  formData,
  setFormData,
  locations,
  locationsDisabled = false,
  timesDisabled = false,
  nowFloor,
  showAdvancedFields = false,
}: {
  formData: OrderFormData;
  setFormData: React.Dispatch<React.SetStateAction<OrderFormData>>;
  locations: Location[] | undefined;
  locationsDisabled?: boolean;
  timesDisabled?: boolean;
  nowFloor?: { date: string; time: string };
  showAdvancedFields?: boolean;
}) {
  const fromLoc = locations?.find((l) => l.id === formData.ship_from_location_id);
  const toLoc = locations?.find((l) => l.id === formData.ship_to_location_id);
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Ship From */}
      <div className="min-w-0 rounded-lg border p-4">
        <h4 className="font-medium mb-3 flex items-center gap-2">
          <Package className="h-4 w-4" /> Ship From
        </h4>
        <LocationPicker
          label="Ship From Location"
          value={formData.ship_from_location_id || null}
          onChange={(locId, loc) => handleShipFromLocationChange(setFormData, locId, loc)}
          required
          disabled={locationsDisabled}
          disabledTooltip="Origin cannot be changed for API-created orders"
        />
        <div className="mt-4">
          <Label>Ready Time *<TzChip value={formData.ready_time} /></Label>
          <WindowRow
            start={formData.ready_time}
            end={formData.ready_time_end}
            required
            nowFloor={nowFloor}
            onStartChange={(newStart) => {
              const { end } = shiftWindowStart(formData.ready_time, formData.ready_time_end, newStart);
              setFormData((prev) => {
                const next = { ...prev, ready_time: newStart, ready_time_end: end };
                // Keep at least a one-hour ready → delivery spread.
                const floor = newStart ? addMinutesLocal(newStart, MIN_LEG_GAP_MINUTES) : "";
                if (floor && next.requested_delivery_time && next.requested_delivery_time < floor) {
                  const { end: dEnd } = shiftWindowStart(
                    next.requested_delivery_time,
                    next.requested_delivery_time_end,
                    floor,
                  );
                  next.requested_delivery_time = floor;
                  next.requested_delivery_time_end = dEnd;
                }
                return next;
              });
            }}
            onEndChange={(newEnd) => {
              setFormData((prev) => ({ ...prev, ready_time_end: clampEnd(prev.ready_time, newEnd) }));
            }}
            disabled={timesDisabled}
          />
        </div>
        {showAdvancedFields && (
          <ShuttleCarrierFields
            idPrefix="order-origin"
            name={formData.origin_shuttle_carrier_name ?? ""}
            dot={formData.origin_shuttle_carrier_dot ?? ""}
            onNameChange={(v) =>
              setFormData((prev) => ({ ...prev, origin_shuttle_carrier_name: v }))
            }
            onDotChange={(v) =>
              setFormData((prev) => ({ ...prev, origin_shuttle_carrier_dot: v }))
            }
            disabled={locationsDisabled}
          />
        )}
      </div>

      {/* Ship To */}
      <div className="min-w-0 rounded-lg border p-4">
        <h4 className="font-medium mb-3 flex items-center gap-2">
          <ArrowRight className="h-4 w-4" /> Ship To
        </h4>
        <LocationPicker
          label="Ship To Location"
          value={formData.ship_to_location_id || null}
          onChange={(locId, loc) => handleShipToLocationChange(setFormData, locations, locId, loc)}
          required
          disabled={locationsDisabled}
          disabledTooltip="Destination cannot be changed for API-created orders"
        />
        <div className="mt-4">
          <Label>Requested Delivery Time *<TzChip value={formData.requested_delivery_time} /></Label>
          <WindowRow
            start={formData.requested_delivery_time}
            end={formData.requested_delivery_time_end}
            required
            minStartTime={formData.ready_time ? formData.ready_time.substring(11, 16) : undefined}
            minStartDate={formData.ready_time ? formData.ready_time.substring(0, 10) : undefined}
            nowFloor={nowFloor}
            onStartChange={(newStart) => {
              const { end } = shiftWindowStart(formData.requested_delivery_time, formData.requested_delivery_time_end, newStart);
              setFormData((prev) => ({
                ...prev,
                requested_delivery_time: newStart,
                requested_delivery_time_end: end,
              }));
            }}
            onEndChange={(newEnd) => {
              setFormData((prev) => ({
                ...prev,
                requested_delivery_time_end: clampEnd(prev.requested_delivery_time, newEnd),
              }));
            }}
            disabled={timesDisabled}
          />
        </div>
        {showAdvancedFields && (
          <ShuttleCarrierFields
            idPrefix="order-destination"
            name={formData.destination_shuttle_carrier_name ?? ""}
            dot={formData.destination_shuttle_carrier_dot ?? ""}
            onNameChange={(v) =>
              setFormData((prev) => ({ ...prev, destination_shuttle_carrier_name: v }))
            }
            onDotChange={(v) =>
              setFormData((prev) => ({ ...prev, destination_shuttle_carrier_dot: v }))
            }
            disabled={locationsDisabled}
          />
        )}
      </div>
    </div>
  );
}

function MultiLegStops({
  formData,
  setFormData,
  locations,
  allowAddStops,
  locationsDisabled,
  timesDisabled = false,
  tractors,
  drivers,
  activeTenant,
  disabledTractorIds,
  disabledTractorIdsByLeg,
  tractorOosStatusById,
  tractorOosRecordsById,
  tractorEligibilityByTractor,
  tractorEligibilityGateState = "off",
  nowFloor,
  onTrailerNeedsServiceBlock,
  showAdvancedFields = false,
  legLocksByIndex,
  legTrailerBaselineById,
}: {
  formData: OrderFormData;
  setFormData: React.Dispatch<React.SetStateAction<OrderFormData>>;
  locations: Location[] | undefined;
  allowAddStops: boolean;
  locationsDisabled?: boolean;
  timesDisabled?: boolean;
  tractors?: Tractor[];
  drivers?: Driver[];
  activeTenant?: string;
  disabledTractorIds?: Set<string>;
  disabledTractorIdsByLeg?: Set<string>[];
  tractorOosStatusById?: Map<
    string,
    import("@/lib/tractor-service-status").TractorServiceStatus
  >;
  tractorOosRecordsById?: Map<
    string,
    import("@/lib/tractor-service-status").ServiceRecordLike[]
  >;
  tractorEligibilityByTractor?: Map<string, TruckEligibilityRow>;
  /** Eligibility feature state; blocks selection while loading/erroring. */
  tractorEligibilityGateState?: EligibilityGateState;
  nowFloor?: { date: string; time: string };
  onTrailerNeedsServiceBlock?: (info: TrailerNeedsServiceBlock) => void;
  showAdvancedFields?: boolean;
  legLocksByIndex?: LegEditLocks[];
  legTrailerBaselineById?: Record<string, string>;
}) {
  const showPerLegAssets = formData.use_same_assets_for_all_legs === false;
  const showPerLegTripType = formData.use_same_trip_type_for_all_legs === false;
  const tripTypeOptions = tripTypeOptionsForTenant(activeTenant);
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-semibold text-muted-foreground">Stops ({formData.legs.length})</h4>
      {formData.legs.map((leg, index) => (
        <div key={leg.id || index} className="border rounded-lg p-4 relative">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold">Stop {index + 1}</h4>
            {formData.legs.length > 2 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => removeLeg(setFormData, index)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p>Delete leg</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {(() => {
            // Per-stop locks shadow the form-wide flags: stop 2 stays
            // editable while stop 1 is in transit or delivered.
            const legLocks = legLocksByIndex?.[index];
            const locationsDisabledLeg = !!locationsDisabled || !!legLocks?.locationsLocked;
            const timesDisabledLeg = !!timesDisabled || !!legLocks?.timesLocked;
            const assetsDisabledLeg = timesDisabledLeg || !!legLocks?.assetsLocked;
            const legFromLoc = locations?.find((l) => l.id === leg.ship_from_location_id);
            const legToLoc = locations?.find((l) => l.id === leg.ship_to_location_id);
            const legHours = stopDurationHours(leg, (local) => formLocalToUtc(local));
            return (
          <>
          {legHours != null && legHours > DURATION_WARNING_THRESHOLD_HOURS && (
            <div
              role="alert"
              className="mb-3 flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-200"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{formatDurationWarning({ legNumber: index + 1, hours: legHours })}</p>
            </div>
          )}
          {showPerLegTripType && (
            <div className="mb-4 max-w-[240px]">
              <Label>Trip Type{formData.status === "confirmed" ? " *" : ""}</Label>
              <Select
                value={leg.trip_type || ""}
                onValueChange={(value) =>
                  updateLeg(setFormData, index, { trip_type: value })
                }
                disabled={assetsDisabledLeg}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select trip type" />
                </SelectTrigger>
                <SelectContent>
                  {tripTypeOptions.map((tt) => (
                    <SelectItem key={tt} value={tt}>{tt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="min-w-0">
              <LocationPicker
                label="Ship From"
                value={leg.ship_from_location_id || null}
                onChange={(locId, loc) => handleLegLocationChange(setFormData, locations, formData, index, "from", locId, loc)}
                required
                disabled={locationsDisabledLeg || index > 0}
                disabledTooltip="This must match the previous stop's destination"
              />
              <div className="mt-3">
                <Label>Ready Time *<TzChip value={leg.ready_time} /></Label>
                <WindowRow
                  start={leg.ready_time}
                  end={leg.ready_time_end}
                  required
                  minStartDate={index > 0 ? formData.legs[index - 1]?.requested_delivery_time_end?.substring(0, 10) : undefined}
                  minStartTime={index > 0 ? formData.legs[index - 1]?.requested_delivery_time_end?.substring(11, 16) : undefined}
                  nowFloor={nowFloor}
                  onStartChange={(newStart) => {
                    const { end } = shiftWindowStart(leg.ready_time, leg.ready_time_end, newStart);
                    updateLeg(setFormData, index, { ready_time: newStart, ready_time_end: end });
                  }}
                  onEndChange={(newEnd) => {
                    updateLeg(setFormData, index, { ready_time_end: clampEnd(leg.ready_time, newEnd) });
                  }}
                  disabled={timesDisabledLeg}
                />
              </div>
              {showAdvancedFields && (
                <ShuttleCarrierFields
                  idPrefix={`leg-${index}-origin`}
                  name={leg.origin_shuttle_carrier_name ?? ""}
                  dot={leg.origin_shuttle_carrier_dot ?? ""}
                  onNameChange={(v) =>
                    updateLeg(setFormData, index, { origin_shuttle_carrier_name: v })
                  }
                  onDotChange={(v) =>
                    updateLeg(setFormData, index, { origin_shuttle_carrier_dot: v })
                  }
                  disabled={locationsDisabledLeg}
                />
              )}
            </div>
            <div className="min-w-0">
              <LocationPicker
                label="Ship To"
                value={leg.ship_to_location_id || null}
                onChange={(locId, loc) => handleLegLocationChange(setFormData, locations, formData, index, "to", locId, loc)}
                required
                disabled={locationsDisabledLeg}
              />
              <div className="mt-3">
                <Label>Delivery Time *<TzChip value={leg.requested_delivery_time} /></Label>
                <WindowRow
                  start={leg.requested_delivery_time}
                  end={leg.requested_delivery_time_end}
                  required
                  minStartDate={leg.ready_time ? leg.ready_time.substring(0, 10) : undefined}
                  minStartTime={leg.ready_time ? leg.ready_time.substring(11, 16) : undefined}
                  nowFloor={nowFloor}
                  onStartChange={(newStart) => {
                    const { end } = shiftWindowStart(leg.requested_delivery_time, leg.requested_delivery_time_end, newStart);
                    updateLeg(setFormData, index, {
                      requested_delivery_time: newStart,
                      requested_delivery_time_end: end,
                    });
                    if (newStart) cascadeDeliveryToNextReady(setFormData, locations, index, newStart);
                  }}
                  onEndChange={(newEnd) => {
                    updateLeg(setFormData, index, {
                      requested_delivery_time_end: clampEnd(leg.requested_delivery_time, newEnd),
                    });
                  }}
                  disabled={timesDisabledLeg}
                />
              </div>
              {showAdvancedFields && (
                <ShuttleCarrierFields
                  idPrefix={`leg-${index}-destination`}
                  name={leg.destination_shuttle_carrier_name ?? ""}
                  dot={leg.destination_shuttle_carrier_dot ?? ""}
                  onNameChange={(v) =>
                    updateLeg(setFormData, index, { destination_shuttle_carrier_name: v })
                  }
                  onDotChange={(v) =>
                    updateLeg(setFormData, index, { destination_shuttle_carrier_dot: v })
                  }
                  disabled={locationsDisabledLeg}
                />
              )}
            </div>
          </div>
          {showAdvancedFields && (
            <div className="mt-4 border-t pt-4">
              <EquipmentAdvancedFields
                idPrefix={`leg-${index}`}
                loadStatus={(leg.load_status ?? "") as LoadStatusValue}
                isTemperatureControlled={leg.is_temperature_controlled === true}
                temperatureF={leg.temperature_f ?? ""}
                onLoadStatusChange={(v) => updateLeg(setFormData, index, { load_status: v })}
                onTemperatureControlledChange={(v) =>
                  updateLeg(setFormData, index, { is_temperature_controlled: v })
                }
                onTemperatureChange={(v) => updateLeg(setFormData, index, { temperature_f: v })}
                disabled={timesDisabledLeg}
              />
            </div>
          )}
          {showPerLegAssets && (
            <div className="mt-4 grid gap-3 sm:grid-cols-3 border-t pt-4">
              <div>
                <Label>Tractor</Label>
                <TractorCombobox
                  tractors={tractors}
                  value={leg.assigned_tractor_id || ""}
                  disabledIds={(() => {
                    // Per-leg set (booked + OOS for this leg's own window)
                    // when the parent computed one; otherwise fall back to
                    // the order-wide set plus a locally computed OOS window.
                    const perLegFromParent = disabledTractorIdsByLeg?.[index];
                    if (perLegFromParent) return perLegFromParent;
                    if (!tractorOosRecordsById || tractorOosRecordsById.size === 0) {
                      return disabledTractorIds;
                    }
                    const legStart = leg.ready_time;
                    const legEnd =
                      leg.requested_delivery_time_end || leg.requested_delivery_time;
                    const perLeg = computeOosBlockedIdsForWindow(
                      tractorOosRecordsById,
                      legStart,
                      legEnd,
                    );
                    return new Set<string>([
                      ...(disabledTractorIds ?? []),
                      ...perLeg,
                    ]);
                  })()}
                  oosStatusById={tractorOosStatusById}
                  eligibilityGateState={tractorEligibilityGateState}
                  eligibilityReasons={(() => {
                    // Per-leg trip type wins; otherwise the order-level one
                    // applies to every leg.
                    const effectiveTripType = showPerLegTripType
                      ? leg.trip_type || ""
                      : formData.trip_type || "";
                    return computeEligibilityReasons(
                      tractorEligibilityByTractor ?? new Map(),
                      effectiveTripType,
                      (tractors ?? []).map((t) => t.id),
                      tractorEligibilityGateState,
                    );
                  })()}
                  disabled={assetsDisabledLeg}
                  onChange={(value) =>
                    updateLeg(setFormData, index, {
                      assigned_tractor_id: value === "none" ? "" : value,
                    })
                  }
                />
              </div>
              <div>
                <Label>Primary Operator</Label>
                <DriverCombobox
                  drivers={drivers}
                  value={leg.assigned_operator_id || ""}
                  onChange={(value) => {
                    const newVal = value === "none" ? "" : value;
                    updateLeg(setFormData, index, {
                      assigned_operator_id: newVal,
                      ...(newVal ? {} : { assigned_operator_2_id: "" }),
                    });
                  }}
                  disabled={assetsDisabledLeg}
                />
              </div>
              <div>
                <Label>Secondary Operator</Label>
                <DriverCombobox
                  drivers={drivers}
                  value={leg.assigned_operator_2_id || ""}
                  onChange={(value) =>
                    updateLeg(setFormData, index, {
                      assigned_operator_2_id: value === "none" ? "" : value,
                    })
                  }
                  disabled={assetsDisabledLeg || !leg.assigned_operator_id || leg.assigned_operator_id === "none"}
                />
              </div>
            </div>
          )}
          {showPerLegAssets && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Trailer ID</Label>
                <TrailerIdField
                  value={leg.trailer_id || ""}
                  onChange={(value) =>
                    updateLeg(setFormData, index, { trailer_id: value })
                  }
                  onNeedsServiceBlock={(info) => onTrailerNeedsServiceBlock?.(info)}
                  disabled={assetsDisabledLeg}
                  placeholder="Trailer number"
                />
              </div>
              <div>
                <Label>Trailer Type</Label>
                <TrailerTypeField
                  trailerId={leg.trailer_id || ""}
                  value={leg.trailer_type_id || ""}
                  onChange={(typeId) =>
                    updateLeg(setFormData, index, { trailer_type_id: typeId })
                  }
                  disabled={assetsDisabledLeg}
                  excludeBobtail
                  originShortCode={legFromLoc?.short_code ?? null}
                  baselineTrailerId={(leg.id && legTrailerBaselineById?.[leg.id]) || ""}
                />
              </div>
            </div>
          )}
          </>
            );
          })()}
        </div>
      ))}
      {allowAddStops && (
        <Button type="button" variant="outline" onClick={() => addLeg(setFormData, formData)} className="w-full">
          <Plus className="h-4 w-4 mr-2" /> Add Stop
        </Button>
      )}
    </div>
  );
}
